import React, { useState, useEffect } from 'react';
import api, { proxyImage } from '../services/api';

interface UpMaster {
  id: number;
  uid: string;
  name: string;
  face: string;
  video_count: number;
  created_at: string;
}

interface BiliSearchResult {
  mid: string;
  uname: string;
  face: string;
  fans: number;
  videos: number;
  sign: string;
  exists: boolean;
}

interface Props {
  upmasters: UpMaster[];
  selectedId?: number;
  onSelect: (um: UpMaster) => void;
  onRequest: () => void; // 申请数据源入口
  isAdmin: boolean; // 当前用户是否管理员（决定「添加」还是「提交申请」）
  onAdded?: () => void; // 添加成功后刷新 upmasters 列表
}

/**
 * 知识库左侧 Up 主列表（替代「切换 Up 主」下拉，支持即时搜索）。
 * 样式参考 .conv-sidebar（会话侧边栏）。
 */
export default function UpMasterSidebar({ upmasters, selectedId, onSelect, onRequest, isAdmin, onAdded }: Props) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BiliSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [msg, setMsg] = useState('');

  const q = query.trim().toLowerCase();
  const filtered = q
    ? upmasters.filter(um => um.name.toLowerCase().includes(q))
    : upmasters;

  // 输入防抖 600ms：本地过滤照旧，非空关键词再实时搜 B站
  useEffect(() => {
    const kw = query.trim();
    if (!kw) {
      setSearchResults(null);
      setSearching(false);
      setSearchError(false);
      return;
    }
    setSearching(true);
    setSearchError(false);
    const timer = window.setTimeout(() => {
      api.searchUpmasters(kw)
        .then(res => {
          setSearchResults(res.data?.data?.results || []);
          setSearching(false);
        })
        .catch(() => {
          setSearchError(true);
          setSearching(false);
        });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [query]);

  const handleAdd = async (item: BiliSearchResult) => {
    setMsg('');
    try {
      const res = await api.addUpMaster(String(item.mid), 'bilibili');
      const id = res.data?.data?.id;
      if (id) api.startCrawl(id).catch(() => {});
      setMsg('已添加，正在后台同步视频');
      onAdded?.();
    } catch {
      setMsg('添加失败，请重试');
    }
  };

  const handleRequest = async (item: BiliSearchResult) => {
    setMsg('');
    try {
      await api.createDataSourceRequest({
        name: item.uname,
        url: `https://space.bilibili.com/${item.mid}`,
        reason: '',
      });
      setMsg('已提交申请，等待管理员审批');
    } catch {
      setMsg('提交申请失败，请重试');
    }
  };

  return (
    <aside className="upmaster-sidebar">
      <div className="upmaster-sidebar-header">
        <input
          type="text"
          className="upmaster-sidebar-search"
          placeholder="搜索 Up 主..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {msg && <div className="upmaster-sidebar-msg">{msg}</div>}
      </div>

      <div className="upmaster-sidebar-list flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="upmaster-sidebar-empty">未找到匹配的 Up 主</div>
        ) : (
          filtered.map(um => (
            <div
              key={um.id}
              className={`upmaster-sidebar-item ${um.id === selectedId ? 'active' : ''}`}
              onClick={() => onSelect(um)}
            >
              <img
                loading="lazy"
                src={proxyImage(um.face, 40, 40)}
                alt={um.name}
                className="upmaster-sidebar-avatar"
              />
              <span className="upmaster-sidebar-main">
                <span className="upmaster-sidebar-name">{um.name}</span>
                <span className="upmaster-sidebar-count">{um.video_count} 个视频</span>
              </span>
            </div>
          ))
        )}
        {q && (
          <>
            <div className="upmaster-sidebar-divider">🔍 B站搜索结果</div>
            {searching && <div className="upmaster-sidebar-search-hint">⏳ 搜索中...</div>}
            {searchError && (
              <div className="upmaster-sidebar-search-hint upmaster-sidebar-search-error">搜索失败</div>
            )}
            {!searching && !searchError && searchResults && searchResults.length === 0 && (
              <div className="upmaster-sidebar-search-hint">未找到相关 Up 主</div>
            )}
            {!searching && !searchError && (searchResults || []).map(item => (
              <div key={item.mid} className="upmaster-sidebar-item upmaster-sidebar-bili">
                <img
                  loading="lazy"
                  src={proxyImage(item.face, 40, 40)}
                  alt={item.uname}
                  className="upmaster-sidebar-avatar"
                />
                <span className="upmaster-sidebar-main">
                  <span className="upmaster-sidebar-name">{item.uname}</span>
                  <span className="upmaster-sidebar-count">粉丝 {item.fans} · 视频 {item.videos}</span>
                </span>
                {item.exists ? (
                  <button className="upmaster-sidebar-action disabled" disabled>已收录</button>
                ) : (
                  <button className="upmaster-sidebar-action add" onClick={() => handleAdd(item)}>＋ 添加</button>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="upmaster-sidebar-footer">
        <button className="upmaster-sidebar-request-btn" onClick={onRequest}>
          ＋ 添加数据源
        </button>
      </div>
    </aside>
  );
}
